import { Button, Kind } from '@thanx/uikit/button'
import { Modal } from '@thanx/uikit/modal'
import { Text } from '@thanx/uikit/text'
import { ReactComponent as Sparkles } from 'assets/images/thanx-ai-sparkles.svg'
import palette from 'constants/palette'
import useDispatch from 'hooks/useDispatch'
import { buildTranslate } from 'locales'
import React from 'react'
import { push } from 'react-router-redux'
import { RECOVERY_AI_ROOT } from 'scenes/RecoveryAI/routes'
import { useStyletron } from 'styletron-react'
import { merchantTheme } from 'theme'
import { NPS_LOOKER_DATA_URL } from './constants'

const t = buildTranslate('components.nps_transition_modal')

export type NpsTransitionModalVariant = 'active' | 'pre_launch'

type Props = {
  isOpen: boolean
  onClose: () => void
  variant: NpsTransitionModalVariant
}

const MODAL_WIDTH_PX = '690px'
const SPARKLE_WIDTH_PX = '44px'
const SPARKLE_HEIGHT_PX = '38px'
const ORDER_FEEDBACK_PATH = '/order_feedback'

const NpsTransitionModal = ({ isOpen, onClose, variant }: Props) => {
  const [css] = useStyletron()
  const dispatch = useDispatch()

  const primaryCtaPath: Record<NpsTransitionModalVariant, string> = {
    active: RECOVERY_AI_ROOT,
    pre_launch: ORDER_FEEDBACK_PATH,
  }

  const handlePrimaryClick = () => {
    dispatch(push(primaryCtaPath[variant]))
    onClose()
  }

  // NPS_LOOKER_DATA_URL is an internal react-router path (this app's own
  // /insights_lab route), not an external URL -- navigate via the router in
  // the same tab, same as the primary CTA, rather than window.open.
  const handleSecondaryClick = () => {
    dispatch(push(NPS_LOOKER_DATA_URL))
    onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      zIndex={merchantTheme.zIndex.modal}
      overrides={{ Dialog: { style: { width: MODAL_WIDTH_PX } } }}
    >
      <div className="p-xl">
        <span
          className={css({
            display: 'inline-block',
            backgroundColor: palette.spearmint10,
            color: palette.spearmint50,
            fontSize: '12px',
            fontWeight: 700,
            letterSpacing: '1.2px',
            textTransform: 'uppercase',
            padding: '6px 12px',
            borderRadius: '5px',
          })}
        >
          {t('badge')}
        </span>
        <Text.Header2
          tag="div"
          color="grey90"
          bold
          className={`d-flex align-items-center mt-m mb-m ${css({
            gap: '10px',
          })}`}
        >
          {t(`${variant}.title`)}
          {/* Decorative — the heading text already conveys the update. */}
          <span
            aria-hidden="true"
            className={css({
              width: SPARKLE_WIDTH_PX,
              height: SPARKLE_HEIGHT_PX,
              flexShrink: 0,
            })}
          >
            <Sparkles className={css({ width: '100%', height: '100%' })} />
          </span>
        </Text.Header2>
        <Text.BodyText2 tag="div" color="grey90" className="mb-m">
          {/* Copy carries <strong>/<em> runs, per CredentialSharingNagModal. */}
          <span dangerouslySetInnerHTML={{ __html: t(`${variant}.body_1`) }} />
        </Text.BodyText2>
        <Text.BodyText2 tag="div" color="grey90" className="mb-l">
          {t('body_2')}
        </Text.BodyText2>
        <div className={`d-flex ${css({ gap: '16px' })}`}>
          <Button kind={Kind.PRIMARY} onClick={handlePrimaryClick}>
            {t(`${variant}.primary_cta`)}
          </Button>
          <Button kind={Kind.SECONDARY} onClick={handleSecondaryClick}>
            {t('secondary_cta')}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default NpsTransitionModal
